import { Play } from '@tunasong/icons'
import type { AugDimWarmupPair } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './aug-dim-warmup-question.module.css'

export interface AugDimWarmupQuestionProps {
  pair: AugDimWarmupPair
  scoreCorrect: number
  scoreAnswered: number
  /** When false, First/Second buttons render disabled (post-answer feedback phase). */
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: 'first' | 'second') => void
}

/**
 * Aug/Dim Warm-up question panel. 2-stimulus comparison — prompt asks "Which
 * was {augmented|diminished}?" with First/Second answer buttons. Stateless;
 * the host owns the session state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md §5.3
 */
export const AugDimWarmupQuestion: FC<AugDimWarmupQuestionProps> = props => {
  const { pair, scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  return (
    <section className={styles.question} aria-label="Aug/Dim warm-up question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>Which was {pair.targetKind}?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.answerRow}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!interactive}
          onClick={() => onAnswer('first')}
        >
          First
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!interactive}
          onClick={() => onAnswer('second')}
        >
          Second
        </Button>
      </div>
    </section>
  )
}
